import { useEffect } from "react";

import { LoadingSpinnerProps } from "../../types/components.types";

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = "Please wait...",
  size = "large",
}) => {
  // Determine SVG dimensions based on size prop.
  const sizeMap = {
    button: 24,
    small: 50,
    medium: 100,
    large: 250,
  };
  const svgSize = sizeMap[size];

  // Start the animation after GEAB logo has rendered on the page.
  useEffect(() => {
    // Get the SVG element via its ID: 'loader-svg'. This is GEAB logo that we will be animating.
    const svg = document.getElementById("loader-svg");
    // If the svg is not found, safely exit early to prevent breaking the app.
    if (!svg) return;

    // Get all the 'path' elements inside the SVG. These paths represent the individual rays of the GEAB logo that will be animated.
    const allRays = Array.from(svg.querySelectorAll("path"));

    // Track the currently highlighted ray index.
    let currentActiveRay = 0;

    // Record the last timestamp of animation to control the animation speed.
    let lastTick = 0;

    // Set how often (in ms) the animation should update.
    const tickInterval = 100;

    // Define the animation function
    const animate = (timestamp: number) => {
      // Check if enough time has passed since the last update.
      if (!lastTick || timestamp - lastTick >= tickInterval) {
        // Reset all rays to their default fill color  #26A6AB.
        allRays.forEach((ray) => {
          ray.setAttribute("fill", "#26A6AB");
        });

        // Set the current active ray to white to make it stand out
        allRays[currentActiveRay].setAttribute("fill", "white");

        // Move to the next ray, looping back to 0 when we reach the end.
        currentActiveRay = (currentActiveRay + 1) % allRays.length;

        // Update the timestamp to record when the last frame was updated,
        lastTick = timestamp;
      }

      // Request the next frame so the animation keeps running smoothly until the 'loading' is complete.
      requestAnimationFrame(animate);
    };

    // Trigger the animation loop.
    const animationId = requestAnimationFrame(animate);

    // Stop the animation if the component unmounts (once the loading is complete).
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className={`loading-spinner-container loading-spinner-container--${size}`}>
      <div className="loading-spinner">
        {/* The SVG graphic that represents GEAB logo. The ID is what is used to target it in the useEffect above. */}
        <svg
          id="loader-svg"
          width={svgSize}
          height={svgSize}
          viewBox="0 0 250 250"
          style={{ maxWidth: "100%", height: "auto" }}
        >
          {/* The radial paths below represent the rays of GEAB logo. */}
          {/* All rays are filled with #26A6AB, except one that starts as 'white' during the animation loop*/}
          <path
            d="M 125 122.5 L 215 120.5 A 4.5 4.5 0 1 1 215 129.5 L 125 127.5 Z"
            fill="#26A6AB"
          />
          <path
            d="M 147.1215596591578 129.3745397753731 L 187.3542285612357 138.6019366464185 A 3 3 0 1 1 185.80131429062055 144.3974916041529 L 146.34510252385025 132.27231725424034 Z"
            fill="#26A6AB"
          />
          <path
            d="M 145.23557158514987 134.95096189432335 L 194.116968793294 160.28589838486225 A 4 4 0 1 1 190.116968793294 167.21410161513775 L 143.73557158514987 137.54903810567665 Z"
            fill="#26A6AB"
          />
          <path
            d="M 141.97056274847715 139.84924240491747 L 172.19937764420203 167.95673695708277 A 3 3 0 1 1 167.95673695708277 172.19937764420203 L 139.8492424049175 141.97056274847713 Z"
            fill="#26A6AB"
          />
          <path
            d="M 137.54903810567665 143.73557158514987 L 167.21410161513775 190.116968793294 A 4 4 0 1 1 160.28589838486225 194.116968793294 L 134.95096189432335 145.23557158514987 Z"
            fill="#26A6AB"
          />
          <path
            d="M 132.27231725424034 146.34510252385025 L 144.3974916041529 185.80131429062055 A 3 3 0 1 1 138.6019366464185 187.3542285612357 L 129.3745397753731 147.1215596591578 Z"
            fill="#26A6AB"
          />
          <path
            d="M 126.5 147.5 L 129 202.5 A 4 4 0 1 1 121 202.5 L 123.5 147.5 Z"
            fill="#26A6AB"
          />
          <path
            d="M 120.62546022462688 147.1215596591578 L 111.3980633535815 187.3542285612357 A 3 3 0 1 1 105.60250839584708 185.80131429062055 L 117.72768274575968 146.34510252385025 Z"
            fill="white"
          />
          <path
            d="M 115.04903810567666 145.23557158514987 L 89.71410161513776 194.116968793294 A 4 4 0 1 1 82.78589838486226 190.116968793294 L 112.45096189432334 143.73557158514987 Z"
            fill="#26A6AB"
          />
          <path
            d="M 110.1507575950825 141.97056274847715 L 82.04326304291725 172.19937764420203 A 3 3 0 1 1 77.80062235579795 167.95673695708277 L 108.02943725152286 139.8492424049175 Z"
            fill="#26A6AB"
          />
          <path
            d="M 106.26442841485013 137.54903810567665 L 59.883031206705995 167.21410161513775 A 4 4 0 1 1 55.883031206705995 160.28589838486225 L 104.76442841485013 134.95096189432335 Z"
            fill="#26A6AB"
          />
          <path
            d="M 103.65489747614975 132.27231725424034 L 64.19868570937945 144.3974916041529 A 3 3 0 1 1 62.64577143876434 138.6019366464185 L 102.87844034084219 129.3745397753731 Z"
            fill="#26A6AB"
          />
          <path d="M 102.5 126.5 L 47.5 129 A 4 4 0 1 1 47.5 121 L 102.5 123.5 Z" fill="#26A6AB" />
          <path
            d="M 102.87844034084218 120.62546022462688 L 62.64577143876433 111.3980633535815 A 3 3 0 1 1 64.19868570937945 105.60250839584708 L 103.65489747614974 117.72768274575968 Z"
            fill="#26A6AB"
          />
          <path
            d="M 104.76442841485013 115.04903810567666 L 55.88303120670601 89.71410161513776 A 4 4 0 1 1 59.88303120670601 82.78589838486226 L 106.26442841485013 112.45096189432334 Z"
            fill="#26A6AB"
          />
          <path
            d="M 108.02943725152286 110.1507575950825 L 77.80062235579794 82.04326304291725 A 3 3 0 1 1 82.04326304291723 77.80062235579795 L 110.1507575950825 108.02943725152286 Z"
            fill="#26A6AB"
          />
          <path
            d="M 112.45096189432333 106.26442841485013 L 82.78589838486222 59.88303120670602 A 4 4 0 1 1 89.71410161513772 55.88303120670602 L 115.04903810567665 104.76442841485013 Z"
            fill="#26A6AB"
          />
          <path
            d="M 117.72768274575968 103.65489747614974 L 105.60250839584711 64.19868570937945 A 3 3 0 1 1 111.3980633535815 62.64577143876433 L 120.62546022462688 102.87844034084218 Z"
            fill="#26A6AB"
          />
          <path d="M 123.5 102.5 L 121 47.5 A 4 4 0 1 1 129 47.5 L 126.5 102.5 Z" fill="#26A6AB" />
        </svg>
        <p className="spinner-loading-message">{message}</p>
      </div>
    </div>
  );
};

export default LoadingSpinner;
